import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import WeeklySection from './components/WeeklySection';
import CategoriesSection from './components/CategoriesSection';
import ReviewsSection from './components/ReviewsSection';
import FaqSection from './components/FaqSection';
import ComicCard from './components/ComicCard';
import { comics } from '@/mocks/comics';

export default function Home() {
  const latest = comics.slice(0, 8);
  const hot = comics.filter((c) => c.hot).slice(0, 3);

  const statusCounts = {
    连载中: comics.filter((c) => c.status === '连载中').length,
    已完结: comics.filter((c) => c.status === '已完结').length,
    即将上线: 3,
  };

  return (
    <>
      <Seo
        title="韩国漫画大全 - 每日更新 · 连载进度同步 · 最新韩漫新作"
        description="韩国漫画大全每日更新、连载进度同步，最新上线韩漫新作第一时间收录，本周更新一目了然，想看韩漫大全最新内容直接进，每日更新不停歇。"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '韩国漫画大全',
            inLanguage: 'zh-CN',
            description: '韩国漫画大全每日更新、连载进度同步，最新上线韩漫新作第一时间收录，本周更新一目了然。',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '韩国漫画大全多久更新一次？', acceptedAnswer: { '@type': 'Answer', text: '我们坚持每日更新不停歇。连载中的作品会在作者更新的第一时间收录同步。' } },
              { '@type': 'Question', name: '「本周更新」是怎么汇总的？', acceptedAnswer: { '@type': 'Answer', text: '本站按周一到周日整理各作品的最新话数与更新时间，形成一目了然的周更新列表。' } },
            ],
          },
        ]}
      />
      <Navbar />

      <main>
        <Hero />

        {/* 最新上线 */}
        <section id="latest" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-8 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-medium tracking-wide text-primary-600">LATEST</span>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">最新上线</h2>
              <p className="mt-1 text-sm text-foreground-600">最新韩漫新作第一时间收录，抢先开看</p>
            </div>
            <Link
              to="/category"
              className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              更多新作 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {latest.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>

        <WeeklySection />

        {/* 热门推荐 */}
        <section id="hot" className="bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <div className="mb-8 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
              <div>
                <span className="text-xs font-medium tracking-wide text-accent-600">HOT PICKS</span>
                <h2 className="mt-1 font-heading text-2xl text-foreground-950 md:text-3xl">热门推荐</h2>
                <p className="mt-1 text-sm text-foreground-600">大家都在追的高分韩漫</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {hot.map((c) => (
                <article
                  key={c.id}
                  className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-50"
                >
                  <div className="relative aspect-[16/9] w-full overflow-hidden">
                    <img
                      src={c.cover}
                      alt={`${c.title} 热门韩漫推荐`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="whitespace-nowrap rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-white">{c.genre}</span>
                      <h3 className="mt-2 text-xl font-semibold text-white">{c.title}</h3>
                      <p className="mt-1 flex items-center gap-3 text-xs text-white/80">
                        <span className="flex items-center gap-1"><i className="ri-star-fill text-secondary-300"></i>{c.rating.toFixed(1)}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{c.views}</span>
                        <span>{c.chapter}</span>
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <CategoriesSection />

        {/* 连载进度概览 */}
        <section id="progress" className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {[
              { key: '连载中', count: statusCounts.连载中, icon: 'ri-play-circle-line', color: 'bg-accent-500', desc: '正在连载的作品，进度实时同步' },
              { key: '已完结', count: statusCounts.已完结, icon: 'ri-checkbox-circle-line', color: 'bg-secondary-500', desc: '已经收官，可一口气看完' },
              { key: '即将上线', count: statusCounts.即将上线, icon: 'ri-timer-flash-line', color: 'bg-primary-500', desc: '新作预告，上线第一时间收录' },
            ].map((s) => (
              <Link
                key={s.key}
                to={`/progress?status=${encodeURIComponent(s.key)}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-accent-300"
              >
                <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white ${s.color}`}>
                  <i className={`${s.icon} text-2xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-600">{s.key}</p>
                  <p className="font-heading text-3xl text-foreground-950">{s.count}<span className="ml-1 text-sm font-normal text-foreground-500">部</span></p>
                  <p className="mt-1 hidden text-xs text-foreground-600 sm:block">{s.desc}</p>
                </div>
                <i className="ri-arrow-right-line ml-auto text-foreground-400 transition-transform group-hover:translate-x-1"></i>
              </Link>
            ))}
          </div>
        </section>

        <ReviewsSection />
        <FaqSection />

        {/* CTA */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=cozy%20reading%20corner%20with%20stacked%20colorful%20Korean%20webtoon%20books%20and%20warm%20lamp%20light%2C%20amber%20orange%20and%20cream%20palette%2C%20soft%20paper%20texture%2C%20inviting%20evening%20atmosphere%2C%20editorial%20photography%20style%2C%20no%20text&width=1600&height=600&seq=cta-kr-2026&orientation=landscape"
              alt="韩国漫画大全 每日更新不停歇"
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35"></div>
          </div>
          <div className="relative mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-24">
            <h2 className="font-heading text-3xl text-white md:text-4xl">每日更新不停歇，追更从今天开始</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/85 md:text-base">
              最新上线韩漫新作、连载进度同步、本周更新一目了然。想看韩漫大全最新内容，直接进来就对了。
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a href="#latest" className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600">
                看最新上线
              </a>
              <Link
                to="/progress"
                className="whitespace-nowrap rounded-full border border-white/40 bg-white/10 px-6 py-3 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                查看连载进度
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}